import { Text, View } from 'react-native';
import React, { FC, useState } from 'react';
import { scale as initialScale } from '@components/frame';
import i18n from 'i18n-js';
import { font as initialFont } from '@styles/fonts';
import Svg, { Circle } from 'react-native-svg';
import { isWeb } from '@helpers';
import { PanelProps } from '@components/operatorPanel/components/panel';

export interface CounterCircleProps extends Pick<PanelProps, 'CC' | 'bgColor' | 'count' | 'scale' | 'font'> {
  size?: number;
  strokeWidth?: number;
  targetCount?: number;
}

const CounterCircle: FC<CounterCircleProps> = ({
  CC,
  size = 342,
  count = 0,
  targetCount,
  strokeWidth = 12,
  scale = initialScale,
  font = initialFont,
}) => {
  const [countBlockHeight, setCountBlockHeight] = useState<number>(0);

  const sizePx = scale(size);
  const scaledStrokeWidth = scale(strokeWidth);
  const radius = (sizePx - scaledStrokeWidth) / 2;
  const innerRadius = radius - scaledStrokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = targetCount ? Math.min(count / targetCount, 1) : 0;
  const strokeDashoffset = circumference * (1 - progress);

  const getCountFont = (): keyof typeof font => {
    const countDigitNumber = count.toString().length;
    if (countDigitNumber <= 2) return 'Counter 1-2 digits';
    if (countDigitNumber <= 5) return `Counter ${countDigitNumber} digits` as keyof typeof font;
    return 'Counter 5 digits';
  };

  const getCountLineHeight = (): number | null => {
    const countDigitNumber = count.toString().length;
    if (countDigitNumber <= 2) return isWeb ? scale(180) : scale(170);
    if (countDigitNumber === 3) return scale(150);
    if (countDigitNumber === 4) return scale(140);
    if (countDigitNumber === 5) return scale(130);
    return null;
  };

  const countFont = getCountFont();
  const countLineHeight = getCountLineHeight();
  const shouldDisplayProgress = targetCount != null;

  return (
    <View style={{ alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={sizePx} height={sizePx} style={{ position: 'absolute' }}>
        {/* Main Circle */}
        <Circle stroke="transparent" fill="rgba(0,0,0,0.70)" cx={sizePx / 2} cy={sizePx / 2} r={innerRadius} />

        {/* Border Circle */}
        {shouldDisplayProgress && (
          <Circle stroke="rgba(0, 0, 0, 0.40)" fill="transparent" cx={sizePx / 2} cy={sizePx / 2} r={radius} strokeWidth={scaledStrokeWidth} />
        )}

        {/* Progress Circle */}
        {shouldDisplayProgress && (
          <Circle
            stroke="#FFFFFF"
            fill="transparent"
            cx={sizePx / 2}
            cy={sizePx / 2}
            r={radius}
            strokeWidth={scaledStrokeWidth}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${sizePx / 2} ${sizePx / 2})`}
          />
        )}
      </Svg>

      <View
        style={{
          width: sizePx,
          height: sizePx,
          borderRadius: sizePx / 2,
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Text style={[font['Body Extra Large'], { color: CC('Label/Dark/Tertiary'), position: 'absolute', top: sizePx * 0.2, textAlign: 'center' }]}>
          {i18n.t('Ready')}
        </Text>

        <Text
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            setCountBlockHeight(height);
          }}
          style={[
            font[countFont],
            {
              color: CC('Label/Dark/AlwaysLight Primary'),
              position: 'absolute',
              top: sizePx / 2,
              transform: [{ translateY: -(countBlockHeight / 2) }],
              opacity: !countBlockHeight ? 0 : 1,
            },
            countLineHeight && { lineHeight: countLineHeight },
          ]}
        >
          {count}
        </Text>

        {!!targetCount && (
          <Text
            style={[
              font['Body Extreme Large'],
              { color: CC('Label/Dark/Tertiary'), position: 'absolute', bottom: sizePx * 0.17, textAlign: 'center' },
            ]}
          >
            {targetCount}
          </Text>
        )}
      </View>
    </View>
  );
};

export default CounterCircle;
